import BENEFICIARY_SUBJECT_TYPE_KEY from '@Domain/Subjects/BeneficiarySubjectTypeKey';
import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import buildSubjectPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectPath';
import Container from '@Guidelines/Component/Container';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import useSubjectAddressParams from '@UI/Pages/Beneficiaries/Hooks/Params/useSubjectAddressParams';
import ImportDocumentSidebar from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Sidebars/ImportDocumentSidebar';
import PreparationContent from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Preparations/Preparation/PreparationContent';
import StepRetryNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Shared/StepRetryNotifications';
import InterviewsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Pages/Interviews/InterviewsNotifications';
import ImportedDocumentsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/ImportedDocumentsNotifications';
import PreparationsNotifications from '@UI/Pages/Beneficiaries/BeneficiaryDetails/Shared/PreparationsNotifications';
import SubjectBreadcrumb from '@UI/Pages/Beneficiaries/Shared/SubjectBreadcrumb';
import * as React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from '@verclam/synapse';

/**
 * Mounted from `…/:dossier/preparations/:preparation`. An invalid dossier address
 * redirects to the beneficiary list, an invalid `preparation` to that dossier — same motif as the other
 * dossier screens.
 */
const Preparation: React.FC = () => {
	const navigate = useNavigate();
	const { subject } = useSubjectAddressParams();
	const { preparation } = useParams<{ preparation: string }>();
	const preparationId = preparation !== undefined ? Number(preparation) : Number.NaN;
	const isInvalidPreparationId = Number.isNaN(preparationId);

	const label = useSelector((state) => {
		const detail = state.workflowRunsStore.workflowRun;

		return detail !== null && detail.id === preparationId ? detail.label : null;
	});

	const breadcrumbItems = React.useMemo(() => [{ label: label ?? 'Préparation' }], [label]);

	React.useEffect(() => {
		if (subject === null) {
			navigate(buildSubjectListPath(BENEFICIARY_SUBJECT_TYPE_KEY));
			return;
		}

		if (isInvalidPreparationId) {
			navigate(buildSubjectPath(subject));
		}
	}, [subject, isInvalidPreparationId, navigate]);

	return (
		<Container>
			<ConditionalLoader data={subject !== null && !isInvalidPreparationId ? subject : null}>
				{(validSubject) => (
					<React.Fragment>
						<SubjectBreadcrumb subject={validSubject} items={breadcrumbItems} />
						<PreparationContent subject={validSubject} preparationId={preparationId} />
						<ImportDocumentSidebar subject={validSubject} />
					</React.Fragment>
				)}
			</ConditionalLoader>
			<ImportedDocumentsNotifications />
			<InterviewsNotifications />
			<PreparationsNotifications />
			<StepRetryNotifications />
		</Container>
	);
};

export default Preparation;
